import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiSave, FiImage, FiArrowLeft } from 'react-icons/fi';
import Layout from '../../../components/admin/Layout';
import axios from '../../../utils/axios';
import ToggleSwitch from '../../../components/common/ToggleSwitch';
import { LanguageContext } from '../../../context/LanguageContext';
import { adminTranslations } from '../../../translations/admin';

const CreateItem = () => {
    const navigate = useNavigate();
    const { language } = useContext(LanguageContext);
    const isRTL = language === 'ar';
    
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        price: '',
        category_id: '',
        is_active: true,
        sort_order: 0,
        image: null,
        color: ''
    });

    const t = useCallback((key) => {
        return adminTranslations[language]?.[key] || key;
    }, [language]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get('/categories');
            setCategories(response.data.data);
        } catch (error) {
            toast.error(t('errorLoadingCategories'));
        }
    }, [t]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked, files } = e.target;
        
        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else if (type === 'file') {
            const file = files[0];
            setFormData(prev => ({ ...prev, [name]: file }));
            
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setImagePreview(null);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    }, [errors]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        const createPromise = new Promise(async (resolve, reject) => {
            try {
                const form = new FormData();
                
                Object.keys(formData).forEach(key => {
                    if (formData[key] !== null && formData[key] !== '') {
                        // Convert boolean is_active to 1/0
                        if (key === 'is_active') {
                            form.append(key, formData[key] ? 1 : 0);
                        } else {
                            form.append(key, formData[key]);
                        }
                    }
                });

                await axios.post('/items', form);
                resolve();
                navigate('/admin/items');
            } catch (error) {
                if (error.response?.status === 422) {
                    setErrors(error.response.data.errors);
                    reject(new Error(t('checkFormErrors')));
                } else {
                    reject(new Error(error.response?.data?.message || t('errorCreatingItem')));
                }
            } finally {
                setLoading(false);
            }
        });

        toast.promise(createPromise, {
            loading: t('saving'),
            success: t('itemCreated'),
            error: (err) => err.message,
        });
    };

    return (
        <Layout>
            <div className={`container mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className={`flex items-center justify-between mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div>
                            <h1 className={`text-3xl font-bold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('createItem')}
                            </h1>
                            <p className={`mt-1 text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('createItemDescription')}
                            </p>
                        </div>
                        <FiImage className="h-8 w-8 text-yellow-500" />
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100">
                        <div className="p-6 space-y-6">
                            {/* Image Upload Section */}
                            <div className="space-y-2">
                                <label className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {t('itemImage')}
                                </label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                                    <div className="space-y-1 text-center">
                                        {imagePreview ? (
                                            <div className="relative inline-block">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="mx-auto h-32 w-32 object-cover rounded-lg"
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <FiImage className="mx-auto h-12 w-12 text-gray-400" />
                                                <div className="flex justify-center text-sm text-gray-600">
                                                    <label
                                                        htmlFor="image-upload"
                                                        className="relative cursor-pointer rounded-md font-medium text-yellow-600 hover:text-yellow-500 focus-within:outline-none"
                                                    >
                                                        <span>{t('uploadImage')}</span>
                                                        <input
                                                            id="image-upload"
                                                            name="image"
                                                            type="file"
                                                            accept="image/*"
                                                            className="sr-only"
                                                            onChange={handleChange}
                                                        />
                                                    </label>
                                                </div>
                                                <p className="text-xs text-gray-500">{t('imageRequirements')}</p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Name Fields */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="name" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('nameEnglish')}
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                    {errors.name && <p className="mt-1.5 text-sm text-red-500">{errors.name[0]}</p>}
                                </div>
                                <div>
                                    <label htmlFor="name_ar" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('nameArabic')}
                                    </label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        id="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                        dir="rtl"
                                    />
                                    {errors.name_ar && <p className="mt-1.5 text-sm text-red-500">{errors.name_ar[0]}</p>}
                                </div>
                            </div>

                            {/* Description Fields */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="description" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('descriptionEnglish')}
                                    </label>
                                    <textarea
                                        name="description"
                                        id="description"
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleChange}
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                    {errors.description && <p className="mt-1.5 text-sm text-red-500">{errors.description[0]}</p>}
                                </div>
                                <div>
                                    <label htmlFor="description_ar" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('descriptionArabic')}
                                    </label>
                                    <textarea
                                        name="description_ar"
                                        id="description_ar"
                                        rows={3}
                                        value={formData.description_ar}
                                        onChange={handleChange}
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                        dir="rtl"
                                    />
                                    {errors.description_ar && <p className="mt-1.5 text-sm text-red-500">{errors.description_ar[0]}</p>}
                                </div>
                            </div>

                            {/* Price and Category */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="price" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('price')}
                                    </label>
                                    <input
                                        type="number"
                                        name="price"
                                        id="price"
                                        value={formData.price}
                                        onChange={handleChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                    {errors.price && <p className="mt-1.5 text-sm text-red-500">{errors.price[0]}</p>}
                                </div>
                                <div>
                                    <label htmlFor="category_id" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('category')}
                                    </label>
                                    <select
                                        name="category_id"
                                        id="category_id"
                                        value={formData.category_id}
                                        onChange={handleChange}
                                        required
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm appearance-none`}
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                            backgroundPosition: isRTL ? 'left 0.5rem center' : 'right 0.5rem center',
                                            backgroundRepeat: 'no-repeat',
                                            backgroundSize: '1.5em 1.5em',
                                            paddingRight: isRTL ? '1rem' : '2.5rem',
                                            paddingLeft: isRTL ? '2.5rem' : '1rem'
                                        }}
                                    >
                                        <option value="">{t('selectCategory')}</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {isRTL ? category.name_ar : category.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.category_id && <p className="mt-1.5 text-sm text-red-500">{errors.category_id[0]}</p>}
                                </div>
                            </div>

                            {/* Sort Order and Status */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="sort_order" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('sortOrder')}
                                    </label>
                                    <input
                                        type="number"
                                        name="sort_order"
                                        id="sort_order"
                                        value={formData.sort_order}
                                        onChange={handleChange}
                                        className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm 
                                            focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                    />
                                    {errors.sort_order && <p className="mt-1.5 text-sm text-red-500">{errors.sort_order[0]}</p>}
                                </div>
                                <div className="flex items-center">
                                    <ToggleSwitch
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        name="is_active"
                                        label={t('status')}
                                    />
                                </div>
                            </div>

                            {/* Color */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-6">
                                <div>
                                    <label htmlFor="color" className={`block text-sm font-medium text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('color')}
                                    </label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <div className="flex-shrink-0 w-12">
                                            <input
                                                type="color"
                                                id="color"
                                                name="color"
                                                value={formData.color || '#FFFFFF'}
                                                onChange={handleChange}
                                                className="block w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            name="color"
                                            value={formData.color || '#FFFFFF'}
                                            onChange={handleChange}
                                            placeholder="#FFFFFF"
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                                            pattern="^#[0-9A-Fa-f]{6}$"
                                            maxLength="7"
                                        />
                                    </div>
                                    {errors.color && <p className="mt-1.5 text-sm text-red-500">{errors.color[0]}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/items')}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                >
                                    <FiArrowLeft className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {t('cancel')}
                                </button>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`inline-flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg
                                        hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 
                                        focus:ring-offset-2 transform transition-all duration-200
                                        ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md active:scale-95'}`}
                                >
                                    <FiSave className={isRTL ? 'ml-2' : 'mr-2'} />
                                    {loading ? t('saving') : t('save')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default CreateItem;
